import { Dispatch, Suspense, lazy, useEffect, useMemo, useState } from 'react';

import { ChakraFlex, useMediaQuery } from '@equip.health/ui';
import { DateTime } from 'luxon';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useHistory, useLocation } from 'react-router-dom';

import { AssessmentEvents } from '~/analytics';
import {
  AssessmentEventAttribute,
  AssessmentEventSource,
} from '~/analytics/assessmentEvents';
import AssessmentsViewModal from '~/components/maud/patients/profile/assessments/common/assessments-results-view/AssessmentsViewModal';
import { InquiryStatusValue } from '~/lib/constants/inquiry.constants';
import { AssessmentAction } from '~/lib/constants/patients.constants';
import { SurveyName } from '~/lib/constants/survey/SurveyName';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { CategorizedAssessments } from '~/lib/types/survey/CategorizedAssessments';
import { GroupedAssessment } from '~/lib/types/survey/GroupedAssessment';
import { categorizeAndSortAssessments } from '~/lib/util/assessmentIndexList.util';
import {
  getCurrentEpisodeChartDomain,
  selectCurrentAssessmentEpisode,
} from '~/lib/util/assessmentTimepoint.util';
import { getSurveyCardsData } from '~/lib/util/survey.util';
import { useAssessmentMenuItemDwell } from '~/lib/hooks/chat/useAssessmentMenuItemDwell';
import { useGetGroupedAssessmentsQuery } from '~/store/services/assessments';
import { useGetAllGroupedAssessmentsV2 } from '~/lib/hooks/useGetAllGroupedAssessmentsV2';
import { useGetDischargePlanByIdQuery } from '~/store/services/clinical-data/dischargePlan';
import {
  useGetPatientClinicalProfileQuery,
  useGetPatientTreatmentEpisodesQuery,
} from '~/store/services/clinicalData';
import {
  useGetInquiryDetailByIdQuery,
  useGetPatientInquiriesQuery,
} from '~/store/services/inquiry';
import { useGetPatientDiagnosesQuery } from '~/store/services/diagnoses';
import AssessmentIndexList from './AssessmentIndexList';
import AssessmentsCardList from './AssessmentsCardList';

const AssessmentsViewModalRedesign = lazy(
  () => import('./assessment-modal/AssessmentsViewModalRedesign'),
);
export type AssessmentsRedesignProps = {
  patientId: string;
  patient: Patient;
  patientPreferredFullName: string;
  // used to create new assessments, probably best as a context
  updateAssessmentState: Dispatch<AssessmentAction>;
};
const TOP_LEVEL_CARD_ID = 'assessment-card';

const AssessmentsRedesignPage = ({
  patient,
  patientId,
  patientPreferredFullName,
  updateAssessmentState,
}: AssessmentsRedesignProps) => {
  const { trackEvent } = useAnalytics();

  const { currentPatientGender: patientGender } = useGlobalContext();
  const {
    assessmentRedesignViewResultsModal:
      isAssessmentRedesignViewResultsModalEnabled,
    assessmentTabUiRevamp: isAssessmentTabUiRevampEnabled,
  } = useFlags<LDFlags>();
  const [isScreenWidthLessThan1000px] = useMediaQuery('(max-width: 1000px)');
  const history = useHistory();
  const location = useLocation();
  const [selectedSurveyId, setSelectedSurveyId] = useState<string | null>(null);
  const [mostRecentClickSurveyId, setMostRecentClickSurveyId] = useState<
    string | null
  >(null);

  const { data: patientClinicalProfileData } =
    useGetPatientClinicalProfileQuery(patientId, {
      skip: !patientId,
    });
  const {
    data: treatmentEpisodesData,
    isLoading: isGetTreatmentEpisodesLoading,
  } = useGetPatientTreatmentEpisodesQuery(patientId, {
    skip: !patientId || !isAssessmentTabUiRevampEnabled,
  });
  const currentAssessmentEpisode = useMemo(
    () => selectCurrentAssessmentEpisode(treatmentEpisodesData?.data ?? []),
    [treatmentEpisodesData?.data],
  );
  const inquiryId = currentAssessmentEpisode?.inquiryId;
  const { data: inquiryDetail, isLoading: isGetInquiryDetailLoading } =
    useGetInquiryDetailByIdQuery(inquiryId ?? '', {
      skip: !isAssessmentTabUiRevampEnabled || !inquiryId,
    });
  const hasTreatmentEpisode = !!currentAssessmentEpisode;
  const { data: patientInquiries, isLoading: isGetPatientInquiriesLoading } =
    useGetPatientInquiriesQuery(patientId, {
      skip:
        !isAssessmentTabUiRevampEnabled ||
        !patientId ||
        isGetTreatmentEpisodesLoading ||
        hasTreatmentEpisode,
    });
  const oldestOpenInquiryDate = useMemo(() => {
    if (!patientInquiries?.length) return undefined;
    const eligible = patientInquiries.filter(
      (i) =>
        i.status?.name !== InquiryStatusValue.ENROLLED &&
        i.status?.name !== InquiryStatusValue.CLOSED,
    );
    if (!eligible.length) return undefined;
    const oldest = eligible.reduce((min, cur) =>
      cur.inquiryDate < min.inquiryDate ? cur : min,
    );
    return oldest.inquiryDate.split('T')[0];
  }, [patientInquiries]);
  const episodeSinceDate = useMemo(() => {
    if (
      isGetTreatmentEpisodesLoading ||
      isGetInquiryDetailLoading ||
      isGetPatientInquiriesLoading
    )
      return undefined;

    const today = DateTime.now().toFormat('yyyy-LL-dd');

    if (currentAssessmentEpisode) {
      const inquiryCreatedAt = inquiryDetail?.createdAt?.split('T')[0];
      if (inquiryCreatedAt) return inquiryCreatedAt;

      const admitDate = currentAssessmentEpisode.admitDate;
      const createdAt = currentAssessmentEpisode.createdAt?.split('T')[0];

      // If admitDate is in the future (episode scheduled but patient not yet
      // admitted), fall through so we still see assessments created before the
      // future admit date.
      if (admitDate && admitDate <= today) return admitDate;
      if (createdAt) return createdAt;
      return today;
    }

    if (oldestOpenInquiryDate) return oldestOpenInquiryDate;
    return today;
  }, [
    isGetTreatmentEpisodesLoading,
    isGetInquiryDetailLoading,
    isGetPatientInquiriesLoading,
    currentAssessmentEpisode,
    inquiryDetail?.createdAt,
    oldestOpenInquiryDate,
  ]);
  const {
    data: groupedAssessmentsV1Data,
    isLoading: isGetGroupedAssessmentsV1Loading,
  } = useGetGroupedAssessmentsQuery(
    { patientId },
    { skip: isAssessmentTabUiRevampEnabled },
  );
  const {
    data: groupedAssessmentsV2Data,
    isLoading: isGetGroupedAssessmentsV2Loading,
  } = useGetAllGroupedAssessmentsV2({
    patientId,
    sinceDate: episodeSinceDate ?? '',
    skip: !isAssessmentTabUiRevampEnabled || !patientId || !episodeSinceDate,
  });
  const groupedAssessmentsData = isAssessmentTabUiRevampEnabled
    ? groupedAssessmentsV2Data
    : groupedAssessmentsV1Data;
  // Diagnoses gate the card list's create/send actions (a restricted survey
  // must open the override modal), so keep the tab in its loading state until
  // diagnoses resolve too. Queried here (RTK dedupes with the card list's own
  // call) so the shared loading spinner can account for it.
  const { isLoading: isGetPatientDiagnosesLoading } =
    useGetPatientDiagnosesQuery(patientId, { skip: !patientId });
  // On the revamp tab the grouped-assessments query is skipped until
  // episodeSinceDate resolves, which itself waits on treatment episodes /
  // inquiry detail / patient inquiries. While skipped the hook reports
  // isLoading=false, so without also counting those prerequisites the tab
  // would drop out of its loading state during that window. Fold the
  // prerequisites (and diagnoses) in so the loading spinner covers the whole
  // load.
  const isGetGroupedAssessmentsLoading = isAssessmentTabUiRevampEnabled
    ? isGetTreatmentEpisodesLoading ||
      isGetInquiryDetailLoading ||
      isGetPatientInquiriesLoading ||
      isGetGroupedAssessmentsV2Loading ||
      isGetPatientDiagnosesLoading
    : isGetGroupedAssessmentsV1Loading || isGetPatientDiagnosesLoading;
  const dischargePlanId = currentAssessmentEpisode?.dischargePlan?.id;
  const { data: dischargePlanData, isLoading: isGetDischargePlanLoading } =
    useGetDischargePlanByIdQuery(dischargePlanId ?? '', {
      skip: !isAssessmentTabUiRevampEnabled || !dischargePlanId,
    });
  const assessmentTabChartDomain = useMemo(
    () =>
      getCurrentEpisodeChartDomain(
        currentAssessmentEpisode,
        groupedAssessmentsData?.surveys ?? [],
      ),
    [currentAssessmentEpisode, groupedAssessmentsData?.surveys],
  );
  // Hold off on building the revamp context until all dependent queries have
  // resolved. Otherwise highlight zones + timeline markers flicker through
  // partial states as each RTK Query response arrives in sequence. (GX-3321)
  const isRevampContextReady =
    isAssessmentTabUiRevampEnabled &&
    !isGetGroupedAssessmentsLoading &&
    !isGetTreatmentEpisodesLoading &&
    !isGetInquiryDetailLoading &&
    !isGetPatientInquiriesLoading &&
    !isGetDischargePlanLoading;
  const assessmentTimepointContext = useMemo(
    () =>
      isRevampContextReady
        ? {
            chartDomain: assessmentTabChartDomain,
            currentEpisode: currentAssessmentEpisode,
            tentativeDischargeDate:
              dischargePlanData?.tentativeDischargeDate ?? null,
          }
        : undefined,
    [
      assessmentTabChartDomain,
      currentAssessmentEpisode,
      dischargePlanData?.tentativeDischargeDate,
      isRevampContextReady,
    ],
  );

  useEffect(() => {
    trackEvent(AssessmentEvents.assessmentsActivityViewed, {
      [AssessmentEventAttribute.PatientId]: patientId,
      [AssessmentEventAttribute.Source]: AssessmentEventSource.AssessmentList,
    });
  }, [patientId, trackEvent]);

  const surveyNameByKey = useMemo(
    () => new Map(Object.entries(SurveyName)),
    [],
  );
  const surveyKeyByName = useMemo(
    () => new Map(Object.entries(SurveyName).map(([k, v]) => [v, k])),
    [],
  );

  const selectAndScrollToSurvey = (surveyId: string) => {
    setSelectedSurveyId(surveyId);
    setMostRecentClickSurveyId(surveyId);
    const el = document.getElementById(`${TOP_LEVEL_CARD_ID}__${surveyId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'auto', block: 'start' });
    }
  };

  const handleIndexSurveyClick = (surveyId: string) => {
    selectAndScrollToSurvey(surveyId);

    const survey = groupedAssessmentsData?.surveys.find(
      (s) => s.externalId === surveyId,
    );
    const enumKey = survey ? surveyKeyByName.get(survey.name) : null;
    if (enumKey) {
      const params = new URLSearchParams(location.search);
      params.set('surveyCode', enumKey);
      history.replace({
        pathname: location.pathname,
        search: params.toString(),
      });
    }
  };

  useEffect(() => {
    if (isGetGroupedAssessmentsLoading || !groupedAssessmentsData?.surveys)
      return;
    const params = new URLSearchParams(location.search);
    const surveyCode = params.get('surveyCode');
    if (!surveyCode) return;

    const surveyDisplayName = surveyNameByKey.get(surveyCode);
    const match = surveyDisplayName
      ? groupedAssessmentsData.surveys.find(
          (s) => s.name === surveyDisplayName,
        )
      : null;
    if (match) {
      selectAndScrollToSurvey(match.externalId);
    }
  }, [isGetGroupedAssessmentsLoading, groupedAssessmentsData?.surveys, location.search]);

  // @deprecated legacy view assessment modal state
  const [viewAssessmentModalData, setViewAssessmentModalData] =
    useState<SurveyDetail | null>(null);

  const categorizedAssessments: CategorizedAssessments = useMemo(() => {
    if (groupedAssessmentsData?.surveys) {
      return categorizeAndSortAssessments(
        groupedAssessmentsData?.surveys,
        isAssessmentTabUiRevampEnabled,
      );
    }
    return {};
  }, [groupedAssessmentsData?.surveys, isAssessmentTabUiRevampEnabled]);

  useAssessmentMenuItemDwell(categorizedAssessments, selectedSurveyId);

  // TODO remove once we create our own view assessment modal.
  // This legacy data structure is what the old assessment modal expects.
  // Do not use this for new features
  // @deprecated
  const surveyResponses = useMemo(() => {
    return groupedAssessmentsData?.surveys.flatMap(
      (groupedAssessment) => groupedAssessment.assessments,
    );
  }, [groupedAssessmentsData?.surveys]);

  // @deprecated
  const legacySurveyCardsData = useMemo(() => {
    if (surveyResponses) {
      return getSurveyCardsData(
        surveyResponses,
        Object.values(SurveyName),
        () => {},
        [],
      );
    }
    return null;
  }, [surveyResponses]);

  const clickedAssessmentData: GroupedAssessment = useMemo(() => {
    if (!viewAssessmentModalData) return null;
    return groupedAssessmentsData.surveys.find(({ assessments }) =>
      assessments.find(
        ({ responseExternalId }) =>
          responseExternalId === viewAssessmentModalData.externalId,
      ),
    );
  }, [groupedAssessmentsData?.surveys, viewAssessmentModalData]);

  return (
    <ChakraFlex maxHeight="100%">
      <AssessmentIndexList
        categorizedAssessments={categorizedAssessments}
        isAssessmentTabUiRevampEnabled={isAssessmentTabUiRevampEnabled}
        isGetGroupedAssessmentsLoading={isGetGroupedAssessmentsLoading}
        isScreenWidthLessThan1000px={isScreenWidthLessThan1000px}
        onSurveyClick={handleIndexSurveyClick}
        selectedSurveyId={selectedSurveyId}
      />
      <AssessmentsCardList
        assessmentTimepointContext={assessmentTimepointContext}
        categorizedAssessments={categorizedAssessments}
        isAssessmentTabUiRevampEnabled={isAssessmentTabUiRevampEnabled}
        isGetGroupedAssessmentsLoading={isGetGroupedAssessmentsLoading}
        mostRecentClickSurveyId={mostRecentClickSurveyId}
        patientId={patientId}
        patientPreferredFullName={patientPreferredFullName}
        setSelectedSurveyId={setSelectedSurveyId}
        setViewAssessmentModalData={setViewAssessmentModalData}
        updateAssessmentState={updateAssessmentState}
      />
      {isAssessmentRedesignViewResultsModalEnabled ? (
        viewAssessmentModalData &&
        clickedAssessmentData && (
          <Suspense>
            <AssessmentsViewModalRedesign
              clickedSurveyDetail={viewAssessmentModalData}
              closeModal={() => setViewAssessmentModalData(null)}
              id="assessment-redesign-view-modal"
              patientPreferredFullName={patientPreferredFullName}
              surveyData={clickedAssessmentData}
              updateAssessmentState={updateAssessmentState}
            />
          </Suspense>
        )
      ) : (
        <AssessmentsViewModal
          assessmentData={legacySurveyCardsData}
          clickedSurveyDetail={viewAssessmentModalData}
          closeModal={() => setViewAssessmentModalData(null)}
          id="assessment-redesign-view-modal"
          patient={patient}
          patientClinicalProfile={patientClinicalProfileData}
          patientGender={patientGender}
          patientPreferredFullName={patientPreferredFullName}
        />
      )}
    </ChakraFlex>
  );
};

export default AssessmentsRedesignPage;
